`
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';
import MapView from '../common/MapView';

const AdminComplaintDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [complaint, setComplaint] = useState(null);
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [afterImages, setAfterImages] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingAssign, setSavingAssign] = useState(false);

  const load = async () => {
    try {
      const data = await complaintService.getComplaint(id);
      const c = data.complaint;
      setComplaint(c);
      setStatus(c.status);
      setAdminNotes(c.adminNotes || '');
      setAssignedTo(c.assignedTo?._id || '');
    } catch (err) {
      toast.error('Failed to load complaint');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onAfterImagesChange = (e) => {
    setAfterImages(Array.from(e.target.files || []));
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    setSavingStatus(true);
    try {
      await complaintService.updateStatus(id, {
        status,
        adminNotes,
        afterImages,
      });
      toast.success('Complaint updated');
      setAfterImages([]);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignedTo) {
      toast.error('Provide admin user ID to assign');
      return;
    }
    setSavingAssign(true);
    try {
      await complaintService.assignComplaint(id, assignedTo);
      toast.success('Complaint assigned');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign');
    } finally {
      setSavingAssign(false);
    }
  };

  if (!complaint) return <LoadingSpinner />;

  const beforeImages = complaint.images?.before || [];
  const afterImagesExisting = complaint.images?.after || [];

  const lat = complaint.location?.coordinates?.[1];
  const lng = complaint.location?.coordinates?.[0];

  return (
    <div className="max-w-5xl mx-auto mt-6 px-4">
      <button
        onClick={() => navigate(-1)}
        className="text-xs text-gray-500 mb-2 hover:underline"
      >
        ← Back
      </button>

      <div className="bg-white rounded-lg shadow p-6 mb-4">
        <div className="flex justify-between mb-3">
          <h2 className="text-xl font-semibold">{complaint.title}</h2>
          <span className="text-xs px-2 py-1 rounded-full bg-gray-100">
            {complaint.status}
          </span>
        </div>
        <p className="text-sm text-gray-700 mb-2">{complaint.description}</p>

        <p className="text-xs text-gray-500 mb-1">
          Category: {complaint.category} | Priority: {complaint.priority}
        </p>
        <p className="text-xs text-gray-500 mb-1">
          Address: {complaint.location?.address}
        </p>
        <p className="text-xs text-gray-500 mb-1">
          Citizen: {complaint.user?.name} ({complaint.user?.email})
        </p>
        <p className="text-xs text-gray-400 mb-1">
          Created: {new Date(complaint.createdAt).toLocaleString()}
        </p>
        {complaint.resolvedAt && (
          <p className="text-xs text-gray-400 mb-1">
            Resolved: {new Date(complaint.resolvedAt).toLocaleString()}
          </p>
        )}
        {complaint.assignedTo && (
          <p className="text-xs text-gray-500 mb-1">
            Assigned to: {complaint.assignedTo.name} (
            {complaint.assignedTo.email})
          </p>
        )}

        <MapView lat={lat} lng={lng} />
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-4">
        <h3 className="font-semibold text-sm mb-2">Images</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium mb-1">Before</p>
            <div className="flex flex-wrap gap-2">
              {beforeImages.length === 0 && (
                <p className="text-xs text-gray-400">No images</p>
              )}
              {beforeImages.map((img) => (
                <img
                  key={img.publicId}
                  src={img.url}
                  alt="before"
                  className="h-24 w-24 object-cover rounded-md"
                />
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium mb-1">After (current)</p>
            <div className="flex flex-wrap gap-2">
              {afterImagesExisting.length === 0 && (
                <p className="text-xs text-gray-400">Not updated yet</p>
              )}
              {afterImagesExisting.map((img) => (
                <img
                  key={img.publicId}
                  src={img.url}
                  alt="after"
                  className="h-24 w-24 object-cover rounded-md"
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs mb-1">
            Upload new &quot;After Cleaning&quot; images
          </label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={onAfterImagesChange}
            className="w-full border px-3 py-2 rounded-md text-xs"
          />
          <p className="text-[10px] text-gray-400 mt-1">
            Uploading new images will replace existing after-images in the
            backend.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {/* Status & notes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-sm mb-3">
            Status & Admin Notes
          </h3>
          <form onSubmit={handleUpdateStatus} className="space-y-3 text-sm">
            <div>
              <label className="block text-xs mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border px-3 py-2 rounded-md text-sm"
              >
                <option value="pending">pending</option>
                <option value="in_progress">in_progress</option>
                <option value="resolved">resolved</option>
                <option value="rejected">rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-xs mb-1">Admin Notes</label>
              <textarea
                rows={4}
                className="w-full border px-3 py-2 rounded-md text-sm"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Internal notes about this complaint and actions taken"
              />
            </div>
            <button
              type="submit"
              disabled={savingStatus}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-xs disabled:opacity-50"
            >
              {savingStatus ? 'Saving...' : 'Save status & notes'}
            </button>
          </form>
        </div>

        {/* Assignment */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-sm mb-3">Assign Complaint</h3>
          <form onSubmit={handleAssign} className="space-y-3 text-sm">
            <div>
              <label className="block text-xs mb-1">
                Admin User ID to assign
              </label>
              <input
                type="text"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full border px-3 py-2 rounded-md text-sm"
                placeholder="Enter admin user _id"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                For now, paste the MongoDB _id of the admin user you want to
                assign. (A nicer dropdown UI can be added later.)
              </p>
            </div>
            <button
              type="submit"
              disabled={savingAssign}
              className="px-4 py-2 bg-purple-600 text-white rounded-md text-xs disabled:opacity-50"
            >
              {savingAssign ? 'Assigning...' : 'Assign complaint'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};


export default AdminComplaintDetail;
`

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';
import MapView from '../common/MapView';

const statusOptions = ['pending', 'in_progress', 'resolved', 'rejected'];

const AdminComplaintDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [complaint, setComplaint] = useState(null);
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [afterImages, setAfterImages] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingAssign, setSavingAssign] = useState(false);

  const load = async () => {
    try {
      const data = await complaintService.getComplaint(id);
      const c = data.complaint;
      setComplaint(c);
      setStatus(c.status);
      setAdminNotes(c.adminNotes || '');
      setAssignedTo(c.assignedTo?._id || '');
    } catch (err) {
      toast.error('Failed to load complaint');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onAfterImagesChange = (e) => {
    setAfterImages(Array.from(e.target.files || []));
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    setSavingStatus(true);
    try {
      await complaintService.updateStatus(id, {
        status,
        adminNotes,
        afterImages,
      });
      toast.success('Complaint updated');
      setAfterImages([]);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignedTo) {
      toast.error('Provide admin user ID to assign');
      return;
    }
    setSavingAssign(true);
    try {
      await complaintService.assignComplaint(id, assignedTo);
      toast.success('Complaint assigned');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign');
    } finally {
      setSavingAssign(false);
    }
  };

  if (!complaint) return <LoadingSpinner />;

  const beforeImages = complaint.images?.before || [];
  const afterImagesExisting = complaint.images?.after || [];

  const lat = complaint.location?.coordinates?.[1];
  const lng = complaint.location?.coordinates?.[0];

  const statusBadgeClass = (s) => {
    switch (s) {
      case 'pending':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'in_progress':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'resolved':
        return 'bg-emerald-50 text-emerald-800 border-emerald-200';
      case 'rejected':
        return 'bg-red-50 text-red-800 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="max-w-6xl mx-auto mt-6 px-4">
      {/* Header + back */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700"
        >
          ← Back to list
        </button>
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${statusBadgeClass(
            complaint.status
          )}`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current mr-1.5" />
          {complaint.status.replace('_', ' ')}
        </span>
      </div>

      {/* Main top card */}
      <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6 mb-4 border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          {/* Complaint info */}
          <div className="flex-1 space-y-2">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 text-xl">
                🗑️
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                  {complaint.title}
                </h2>
                <p className="text-xs text-gray-500 mb-1">
                  Category: <span className="capitalize">{complaint.category}</span> •
                  Priority: <span className="capitalize">{complaint.priority}</span>
                </p>
                <p className="text-xs text-gray-500 mb-1">
                  Address: {complaint.location?.address}
                </p>
                <p className="text-[11px] text-gray-400">
                  Created:{' '}
                  {new Date(complaint.createdAt).toLocaleString()}
                  {complaint.resolvedAt && (
                    <>
                      {'  •  Resolved: '}
                      {new Date(
                        complaint.resolvedAt
                      ).toLocaleString()}
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="mt-2 text-sm text-gray-700">
              <p className="font-semibold text-xs text-gray-600 mb-1">
                Citizen Description
              </p>
              <p className="whitespace-pre-line">
                {complaint.description}
              </p>
            </div>
          </div>

          {/* Citizen + assignment info */}
          <div className="w-full lg:w-72 bg-indigo-50/60 border border-indigo-100 rounded-2xl p-4 text-xs space-y-2">
            <p className="font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-lg">👤</span>
              Citizen Details
            </p>
            <p className="text-gray-700">
              {complaint.user?.name || 'Unknown citizen'}
            </p>
            <p className="text-gray-500">
              {complaint.user?.email || 'No email'}{' '}
              {complaint.user?.phone && (
                <>• {complaint.user.phone}</>
              )}
            </p>
            <div className="h-px bg-indigo-100 my-2" />
            <p className="font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-lg">🧑‍💼</span>
              Assigned To
            </p>
            {complaint.assignedTo ? (
              <>
                <p className="text-gray-700">
                  {complaint.assignedTo.name}
                </p>
                <p className="text-gray-500">
                  {complaint.assignedTo.email}
                </p>
              </>
            ) : (
              <p className="text-gray-500">
                Not assigned yet. Use the panel below to assign.
              </p>
            )}
            {complaint.adminNotes && (
              <>
                <div className="h-px bg-indigo-100 my-2" />
                <p className="font-semibold text-gray-800">
                  Notes (latest)
                </p>
                <p className="text-gray-600 whitespace-pre-line max-h-24 overflow-y-auto">
                  {complaint.adminNotes}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Map + images */}
      <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6 mb-4 border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">
          Location & Field Photos
        </h3>

        <MapView lat={lat} lng={lng} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {/* Before images */}
          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">
              Before Cleaning
            </p>
            <div className="flex flex-wrap gap-2">
              {beforeImages.length === 0 && (
                <p className="text-xs text-gray-400">
                  No images uploaded by citizen.
                </p>
              )}
              {beforeImages.map((img) => (
                <img
                  key={img.publicId}
                  src={img.url}
                  alt="before"
                  className="h-24 w-24 object-cover rounded-md border"
                />
              ))}
            </div>
          </div>

          {/* After images (current) */}
          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">
              After Cleaning (Current)
            </p>
            <div className="flex flex-wrap gap-2">
              {afterImagesExisting.length === 0 && (
                <p className="text-xs text-gray-400">
                  Not updated yet. Upload new images after cleaning.
                </p>
              )}
              {afterImagesExisting.map((img) => (
                <img
                  key={img.publicId}
                  src={img.url}
                  alt="after"
                  className="h-24 w-24 object-cover rounded-md border"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Upload new after-images */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Upload new &quot;After Cleaning&quot; images
          </label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={onAfterImagesChange}
            className="w-full border px-3 py-2 rounded-md text-xs bg-gray-50"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            New uploads will replace the existing after-images for this
            complaint. Limit to 5 clear photos if possible.
          </p>
        </div>
      </div>

      {/* Status + Assign panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {/* Status & Notes */}
        <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6 border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">
            Update Status & Internal Notes
          </h3>
          <form
            onSubmit={handleUpdateStatus}
            className="space-y-3 text-sm"
          >
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border px-3 py-2 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Admin Notes
              </label>
              <textarea
                rows={4}
                className="w-full border px-3 py-2 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Internal notes about site visit, actions taken, next steps, etc."
              />
            </div>
            <button
              type="submit"
              disabled={savingStatus}
              className="px-4 py-2 bg-indigo-600 text-white rounded-full text-xs sm:text-sm disabled:opacity-60 hover:bg-indigo-700 shadow-sm"
            >
              {savingStatus ? 'Saving...' : 'Save status & notes'}
            </button>
          </form>
        </div>

        {/* Assign */}
        <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6 border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">
            Assign Complaint to Admin
          </h3>
          <form
            onSubmit={handleAssign}
            className="space-y-3 text-sm"
          >
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Admin User ID
              </label>
              <input
                type="text"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full border px-3 py-2 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                placeholder="Paste MongoDB _id of the admin user"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                For now, paste the <code>_id</code> of an existing admin from
                the users collection. Later you can add a dropdown list of
                admins.
              </p>
            </div>
            <button
              type="submit"
              disabled={savingAssign}
              className="px-4 py-2 bg-purple-600 text-white rounded-full text-xs sm:text-sm disabled:opacity-60 hover:bg-purple-700 shadow-sm"
            >
              {savingAssign ? 'Assigning...' : 'Assign complaint'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminComplaintDetail;
